import { TitleCasePipe } from '@angular/common';
import { Component, computed, inject, signal, Output, EventEmitter, effect, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { PaginatorModule } from '@openng/optimus-ui/paginator';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TableModule } from '@openng/optimus-ui/table';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { Lot, OUnitCodeService, Project } from '@zayan-erp/home-building';
import { ScheduleLotFiltersCollapsable, TaskStatus, OrderEntities, SortByEntities } from '@zayan-erp/scheduler';
import { SchedulerService } from '@zayan-erp/scheduler';
import { LotsService } from '@zayan-erp/home-building';
import { tap } from 'rxjs';
import { ProjectsService } from '@zayan-erp/home-building';
@Component({
  selector: 'app-schedule-filter',
  standalone: true,
  imports: [
    NgSelectModule,
    SelectButtonModule,
    ToggleSwitchModule,
    TableModule,
    DatePickerModule,
    RouterModule,
    ReactiveFormsModule,
    TitleCasePipe,
    FormsModule,
    PaginatorModule
  ],
  templateUrl: './schedule-filter.component.html',
  styleUrl: './schedule-filter.component.css'
})
export class ScheduleFilterComponent {
  @Output() filtersChanged = new EventEmitter<ScheduleLotFiltersCollapsable>();
  #ounitCodeService = inject(OUnitCodeService);
  #activatedRoute = inject(ActivatedRoute);
  #schedulerService = inject(SchedulerService);
  #lotsService = inject(LotsService);
  #router = inject(Router);
  #projectsService = inject(ProjectsService);

  selectedFiltersCount = output<number>();

  organizations = toSignal(this.#ounitCodeService.readOUnitCodes(), { initialValue: [] });
  selectedOrganization = signal<string | null>(
    this.#activatedRoute.snapshot.queryParamMap.get('organizationId') ?? null
  );
  projects = computed(() => this.projectRXResource.value() ?? []);
  selectedProject = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('projectId') ?? null);
  lots = computed(() => this.lotsRxResource.value() ?? []);
  selectedLots = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('lotId') ?? null);
  schedule = toSignal(this.#schedulerService.readSchedule(), { initialValue: [] });
  selectedSchedule = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('scheduleId') ?? null);
  tasks = toSignal(this.#schedulerService.readTasks(), { initialValue: [] });
  selectedTask = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('taskId') ?? null);
  stages = toSignal(this.#schedulerService.readStages(), { initialValue: [] });
  selectedStage = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('stageId') ?? null);
  statuses = signal(TaskStatus);
  selectedStatus = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('selectedStatus') ?? '0');
  sortBy = signal(SortByEntities);
  selectedSortBy = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('selectedSortBy') ?? null);
  orderType = signal(OrderEntities);
  selectedOrder = signal<string | number>(this.#activatedRoute.snapshot.queryParamMap.get('selectedOrder') ?? '0');

  projectRXResource = rxResourceFixed({
    params: () => ({ organizationId: this.selectedOrganization() }),
    defaultValue: [],
    stream: ({ params }) => {
      let apiCall$ = !params.organizationId
        ? this.#projectsService.readProjects()
        : this.#ounitCodeService.readOunitProjects(params.organizationId!);
      return apiCall$.pipe(tap((response: Project[]) => {}));
    }
  });

  lotsRxResource = rxResourceFixed({
    params: () => ({ projectId: this.selectedProject() }),
    defaultValue: [],
    stream: ({ params }) => {
      let apiCall$ = !params.projectId
        ? this.#lotsService.readLots()
        : this.#projectsService.getProjectLots(params.projectId!, false);
      return apiCall$.pipe(tap((response: Lot[]) => {}));
    }
  });

  // Add effect to emit changes
  onFiltersChange = effect(() => {
    const filters: ScheduleLotFiltersCollapsable = {
      OUnitCodeId: this.selectedOrganization(),
      ProjectId: this.selectedProject(),
      LotId: this.selectedLots(),
      ScheduleId: this.selectedSchedule(),
      TaskId: this.selectedTask(),
      StageId: this.selectedStage(),
      StatusBy: this.selectedStatus(),
      SortBy: this.selectedSortBy(),
      SortOrder: this.selectedOrder()
    };

    const queryParams: any = {};
    const currentParams: any = { ...(this.#router.getCurrentNavigation()?.finalUrl?.queryParams || {}) };

    [
      { signal: this.selectedOrganization, key: 'organizationId' },
      { signal: this.selectedProject, key: 'projectId' },
      { signal: this.selectedLots, key: 'lotId' },
      { signal: this.selectedSchedule, key: 'scheduleId' },
      { signal: this.selectedTask, key: 'taskId' },
      { signal: this.selectedStage, key: 'stageId' },
      { signal: this.selectedStatus, key: 'statusBy' },
      { signal: this.selectedSortBy, key: 'sortBy' },
      { signal: this.selectedOrder, key: 'sortOrder' }
    ].forEach(({ signal, key }) => {
      const value = signal();
      value ? (queryParams[key] = value) : delete currentParams[key];
    });

    const allQueryParams = { ...currentParams, ...queryParams };

    this.selectedFiltersCount.emit(
      Object.keys(allQueryParams).filter(key => !['sortOrder', 'statusBy'].includes(key)).length +
        (allQueryParams.statusBy === '0' ? 0 : 1)
    );
    this.#router.navigate([], { queryParams: allQueryParams, replaceUrl: true });
    this.filtersChanged.emit(filters);
  });
}
